import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ArrowRight, HeadphonesIcon, Shield, Clock, Users, CheckCircle } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';

export function OngoingSupport() {

  const supportFeatures = [
    {
      icon: <Clock className="h-8 w-8 text-blue-600" />,
      title: "24/7 Availability",
      description: "Round-the-clock support when you need it most"
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: "Proactive Monitoring",
      description: "We monitor your systems to prevent issues"
    },
    {
      icon: <Users className="h-8 w-8 text-blue-600" />,
      title: "Expert Team",
      description: "Certified Zoho professionals at your service"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full mb-4">
            <HeadphonesIcon className="h-4 w-4 mr-2" />
            Support Services
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Always Here When You Need Us
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Don't let technical issues slow you down. Our comprehensive support plans 
            ensure your Zoho systems run smoothly 24/7.
          </p>
        </div>

        {/* Support Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {supportFeatures.map((feature, index) => (
            <Card key={index} className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="mb-4 flex justify-center">{feature.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Support Benefits */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Comprehensive Support Services
              </h3>
              <p className="text-gray-600 mb-6">
                Our dedicated support team ensures your Zoho systems run smoothly and efficiently, 
                providing expert assistance whenever you need it.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">24/7 technical support and issue resolution</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">Performance monitoring and optimization</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">Regular system health checks and maintenance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">User training and feature adoption assistance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">Priority support for critical business operations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">Quarterly optimization reviews and recommendations</span>
                </div>
              </div>
            </div>

            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              Get Custom Support Quote
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          <div className="relative">
            <ImageWithFallback 
              src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=600&h=500&fit=crop"
              alt="Support team helping clients"
              className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
            />
            
            {/* Support Stats */}
            <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
              <div className="text-2xl font-bold text-green-600">&lt;2min</div>
              <div className="text-sm text-gray-600">Avg Response</div>
            </div>
            
            <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
              <div className="text-2xl font-bold text-blue-600">99.9%</div>
              <div className="text-sm text-gray-600">Uptime SLA</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}